import React, { useState, useEffect, useMemo } from "react";
import { apiGet, apiPost, apiDelete } from "../../../api";
import {
  FiX,
  FiUser,
  FiAward,
  FiBookOpen,
  FiSend,
  FiInfo,
  FiSearch,
  FiUserCheck,
  FiUserX,
} from "react-icons/fi";
import "../styles/FindClassmates.css";

const tabs = ["All", "F", "W", "S", "Y"];
const termLabel = (t) => {
  switch (t) {
    case "F":
      return "Fall";
    case "W":
      return "Winter";
    case "S":
      return "Summer";
    case "Y":
      return "Year-long";
    default:
      return "All";
  }
};
const anonymizeEmail = (email) =>
  email && email.includes("@") ? email.split("@")[0] : email;

/* === Connect Button Component === */
function ConnectionButton({ info, onConnect, onCancelRequest }) {
  const [cancelState, setCancelState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset cancel state after 2 seconds
  useEffect(() => {
    if (cancelState) {
      const timer = setTimeout(() => {
        setCancelState(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [cancelState]);

  const handleClick = async () => {
    if (info.connectionStatus === "friend") {
      // Do nothing - already friends
      return;
    } else if (info.connectionStatus === "sent") {
      if (!cancelState) {
        // First click: show "Cancel request?"
        setCancelState(true);
      } else {
        // Second click: actually cancel
        setIsLoading(true);
        try {
          await onCancelRequest(info.requestId);
          setCancelState(false);
        } catch (error) {
          console.error("Failed to cancel request:", error);
        } finally {
          setIsLoading(false);
        }
      }
    } else if (info.connectionStatus === "received") {
      // User has received a request from this person - direct to inbox
      return;
    } else {
      // No connection - show connect modal
      onConnect(info);
    }
  };

  const getButtonContent = () => {
    if (info.connectionStatus === "friend") {
      return {
        text: "Friends",
        icon: <FiUserCheck />,
        className: "btn-friend",
        disabled: true,
      };
    } else if (info.connectionStatus === "sent") {
      return {
        text: cancelState ? "Cancel request?" : "Requested",
        icon: cancelState ? <FiUserX /> : <FiSend />,
        className: cancelState ? "btn-cancel" : "btn-requested",
        disabled: isLoading,
      };
    } else if (info.connectionStatus === "received") {
      return {
        text: "Received",
        icon: <FiInfo />,
        className: "btn-received",
        disabled: false,
      };
    } else {
      return {
        text: "Connect",
        icon: <FiSend />,
        className: "btn-secondary",
        disabled: false,
      };
    }
  };

  const buttonConfig = getButtonContent();

  return (
    <button
      className={`btn connect-btn ${buttonConfig.className}`}
      onClick={handleClick}
      disabled={buttonConfig.disabled}
    >
      {buttonConfig.icon}
      <span>{buttonConfig.text}</span>
    </button>
  );
}

/* === Connect Modal === */
function ConnectModal({ toUser, me, onClose }) {
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const userHandle = useMemo(() => {
    if (me?.preferredPlatform === "DISCORD" && me?.discordHandle) {
      return `(${me.discordHandle})`;
    }
    if (me?.preferredPlatform === "INSTAGRAM" && me?.instagramHandle) {
      return `(${me.instagramHandle})`;
    }
    return "";
  }, [me]);

  if (!toUser) return null;

  const doSend = async () => {
    setError(""); // Clear any previous errors
    try {
      await apiPost("/connect", {
        toUserId: toUser.userId,
        platform: me?.preferredPlatform,
        message,
      });
      setSent(true);
    } catch (e) {
      setError(e.message || "Failed to send");
    }
  };

  return (
    <div className="modal-backdrop" onClick={() => onClose(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={() => onClose(false)}>
          <FiX />
        </button>
        {!sent ? (
          <>
            <h3 className="modal-header">
              Connect with {anonymizeEmail(toUser.email)}
            </h3>
            <p>They will see the following information:</p>
            <div className="connection-preview">
              <div className="preview-row">
                <FiUser /> <strong>{me?.name || "(Your name not set)"}</strong>
              </div>
              <div className="preview-row">
                <FiAward />{" "}
                {[me?.degree, me?.major].filter(Boolean).join(", ") ||
                  "(Degree/Major not set)"}
              </div>
              {me?.yearOfStudy && (
                <div className="preview-row">
                  <FiInfo /> Year {me.yearOfStudy}
                </div>
              )}
              <div className="preview-row">
                <FiSend />{" "}
                <strong>{me?.preferredPlatform || "(Platform not set)"}</strong>{" "}
                {userHandle}
              </div>
              <div className="preview-divider" />
              <div className="preview-row course-list">
                <FiBookOpen />
                <div>
                  You share{" "}
                  <strong>
                    {toUser.sharedCourseCount} course
                    {toUser.sharedCourseCount !== 1 && "s"}
                  </strong>{" "}
                  together:
                  <ul className="shared-courses-modal-list">
                    {(toUser.sharedCourseCodes || []).map((code) => (
                      <li key={code}>{code}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <textarea
              rows={3}
              maxLength={100}
              placeholder="Add a short message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="modal-textarea"
            />
            <div className="char-counter">{message.length}/100</div>
            {error && <p className="modal-error">{error}</p>}
            <div className="modal-actions">
              <button
                onClick={() => onClose(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={doSend} className="btn btn-primary">
                Send Request
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="modal-header">Connection Request Sent!</h3>
            <p>Your request has been sent to {anonymizeEmail(toUser.email)}.</p>
            <div className="modal-actions">
              <button onClick={() => onClose(true)} className="btn btn-primary">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* === Buckets (Results Display) === */
function Buckets({ result, onConnect, onCancelRequest, term }) {
  const byCourseCount = result?.buckets?.byCourseCount || {};
  const exactCourses = result?.buckets?.exactCourses || [];
  const detailed = Array.isArray(result?.detailed) ? result.detailed : [];
  const detailMap = new Map(detailed.map((d) => [d.email, d]));

  const exactMatchSet = useMemo(() => new Set(exactCourses), [exactCourses]);

  if (detailed.length === 0) {
    return null;
  }

  // Helper function for the 'Share 1 course' case
  const groupByCourse = (emails) => {
    const map = {};
    emails.forEach((email) => {
      const info = detailMap.get(email);
      if (!info) return;
      (info.sharedCourseCodes || []).forEach((code) => {
        if (!map[code]) map[code] = [];
        map[code].push(info);
      });
    });

    // Sort students within each course by section matches
    Object.keys(map).forEach((course) => {
      map[course].sort((a, b) => {
        // Check if each student has a section match for this course
        const aHasMatch = (a.sharedSectionMatchCodes || [])
          .map((full) => full.split(" ").slice(0, 2).join(" "))
          .includes(course);
        const bHasMatch = (b.sharedSectionMatchCodes || [])
          .map((full) => full.split(" ").slice(0, 2).join(" "))
          .includes(course);

        // Sort: section matches first (true > false)
        return (bHasMatch ? 1 : 0) - (aHasMatch ? 1 : 0);
      });
    });

    return map;
  };

  return (
    <div className="results-buckets">
      {Object.keys(byCourseCount)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => {
          const emails = byCourseCount[k] || [];
          const filteredEmails = emails.filter(
            (email) => !exactMatchSet.has(email)
          );
          if (filteredEmails.length === 0) return null;

          const renderStudentList = (infos) => (
            <ul className="classmate-list">
              {infos.map((info) => (
                <li key={info.email} className="classmate-item">
                  <span>{anonymizeEmail(info.email)}</span>
                  {info?.userId && (
                    <ConnectionButton
                      info={info}
                      onConnect={onConnect}
                      onCancelRequest={onCancelRequest}
                    />
                  )}
                </li>
              ))}
            </ul>
          );

          return (
            <details key={k} className="classmate-bucket">
              <summary>
                Share {k} course{k === "1" ? "" : "s"} ({filteredEmails.length}{" "}
                classmates)
              </summary>
              <div className="bucket-content">
                {/* Case for 1 shared course (groups by course code) */}
                {k === "1" &&
                  Object.entries(groupByCourse(filteredEmails)).map(
                    ([course, infos]) => {
                      // Count how many students have section matches for this course
                      const sectionMatchCount = infos.filter((info) =>
                        (info.sharedSectionMatchCodes || [])
                          .map((full) => full.split(" ").slice(0, 2).join(" "))
                          .includes(course)
                      ).length;

                      return (
                        <details key={course} className="sub-bucket">
                          <summary>
                            {course} ({infos.length})
                            {sectionMatchCount > 0 && (
                              <span className="section-match-badge">
                                ({sectionMatchCount} in same section)
                              </span>
                            )}
                          </summary>
                          <ul className="classmate-list circle-bullets">
                            {infos.map((info) => {
                              // Check if this student has section match
                              const hasMatch = (
                                info.sharedSectionMatchCodes || []
                              )
                                .map((full) =>
                                  full.split(" ").slice(0, 2).join(" ")
                                )
                                .includes(course);

                              return (
                                <li key={info.email} className="classmate-item">
                                  <span
                                    className={
                                      hasMatch ? "course-code-highlight" : ""
                                    }
                                  >
                                    {anonymizeEmail(info.email)}
                                  </span>
                                  {info?.userId && (
                                    <ConnectionButton
                                      info={info}
                                      onConnect={onConnect}
                                      onCancelRequest={onCancelRequest}
                                    />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </details>
                      );
                    }
                  )}

                {/* ENHANCED: Case for 2+ shared courses with section sorting and highlighting */}
                {Number(k) >= 2 &&
                  filteredEmails
                    .map((email) => detailMap.get(email))
                    .filter(Boolean)
                    // SORT BY: Number of exact section matches (descending)
                    .sort((a, b) => {
                      const exactCountA =
                        a.sharedSectionMatchCodes?.length || 0;
                      const exactCountB =
                        b.sharedSectionMatchCodes?.length || 0;
                      return exactCountB - exactCountA;
                    })
                    .map((info) => {
                      // Create a Set of BASE course codes that have exact section matches
                      // e.g., "EECS 3482 A" -> extract "EECS 3482"
                      const exactSectionBaseCodes = new Set(
                        (info.sharedSectionMatchCodes || []).map(
                          (fullCode) =>
                            fullCode.split(" ").slice(0, 2).join(" ") // "EECS 3482 A" -> "EECS 3482"
                        )
                      );

                      return (
                        <details
                          key={info.email}
                          className="sub-bucket wide-summary"
                        >
                          <summary>
                            {anonymizeEmail(info.email)}
                            {/* Show section match count if any */}
                            {info.sharedSectionMatchCodes &&
                              info.sharedSectionMatchCodes.length > 0 && (
                                <span className="section-match-badge">
                                  {info.sharedSectionMatchCodes.length} exact
                                  section
                                  {info.sharedSectionMatchCodes.length !== 1
                                    ? "s"
                                    : ""}
                                </span>
                              )}
                            {info?.userId && (
                              <ConnectionButton
                                info={info}
                                onConnect={onConnect}
                                onCancelRequest={onCancelRequest}
                              />
                            )}
                          </summary>
                          <ul className="course-code-list">
                            {/* HIGHLIGHTING LOGIC: Mark courses with exact section matches */}
                            {(info.sharedCourseCodes || []).map((course) => {
                              const isExactMatch =
                                exactSectionBaseCodes.has(course);
                              return (
                                <li key={course}>
                                  <span
                                    className={
                                      isExactMatch
                                        ? "course-code-highlight"
                                        : "course-code-normal"
                                    }
                                  >
                                    {course}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </details>
                      );
                    })}
              </div>
            </details>
          );
        })}

      {/* Exact Schedule Match section */}
      {exactCourses.length > 0 && (
        <details className="classmate-bucket exact-match">
          <summary>
            Exact Schedule Match ({exactCourses.length} classmates)
          </summary>
          <div className="bucket-content">
            <ul className="classmate-list">
              {exactCourses.map((email) => {
                const info = detailMap.get(email);
                return (
                  <li key={email} className="classmate-item">
                    <span>{anonymizeEmail(email)}</span>
                    {info?.userId && (
                      <ConnectionButton
                        info={info}
                        onConnect={onConnect}
                        onCancelRequest={onCancelRequest}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}

/* === Main Component === */
export default function FindClassmates() {
  const [tab, setTab] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [allResults, setAllResults] = useState(null);
  const [me, setMe] = useState(null);
  const [connectTarget, setConnectTarget] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    apiGet("/auth/me")
      .then((d) => setMe(d.user))
      .catch(() => {});
  }, []);

  const runSearch = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    setAllResults(null);
    setSearched(false);
    try {
      if (tab === "All") {
        const terms = ["F", "W", "S", "Y"];
        const settled = await Promise.allSettled(
          terms.map((t) => apiGet(`/classmates?term=${t}`))
        );
        const out = {};
        settled.forEach((r, i) => {
          if (r.status === "fulfilled" && r.value) out[terms[i]] = r.value;
        });
        setAllResults(out);
      } else {
        const data = await apiGet(`/classmates?term=${tab}`);
        setResult(data);
      }
    } catch (e) {
      setError(e.message || "Failed to search");
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await apiDelete(`/connect/${requestId}`);
      // Refresh the search results after canceling
      await runSearch();
    } catch (e) {
      setError(e.message || "Failed to cancel request");
      throw e; // Re-throw to let the button component handle loading state
    }
  };

  const hasResults = useMemo(() => {
    if (tab === "All") {
      return (
        allResults &&
        Object.values(allResults).some(
          (termResult) => termResult?.detailed?.length > 0
        )
      );
    } else {
      return result?.detailed?.length > 0;
    }
  }, [result, allResults, tab]);

  return (
    <div className="find-classmates-container page-card">
      <div className="classmates-header">
        <h2>Find Classmates</h2>
        <div className="tabs">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`tab-btn ${tab === t ? "active" : ""}`}
            >
              {termLabel(t)}
            </button>
          ))}
        </div>
      </div>

      <div className="search-actions">
        <button
          onClick={runSearch}
          disabled={loading}
          className="btn btn-primary"
        >
          <FiSearch />
          {loading ? "Searching..." : "Find Classmates"}
        </button>
      </div>

      {error && <p className="status-message error">{error}</p>}

      <div className="results-container">
        {searched && !loading && !error && !hasResults && (
          <div className="no-results-msg">
            <p>No classmates found.</p>
            <p>Check again when we have more users!</p>
          </div>
        )}

        {result && tab !== "All" && (
          <Buckets
            result={result}
            onConnect={setConnectTarget}
            onCancelRequest={handleCancelRequest}
            term={tab}
          />
        )}
        {allResults &&
          tab === "All" &&
          ["F", "W", "S", "Y"].map(
            (t) =>
              allResults[t] &&
              allResults[t].detailed.length > 0 && (
                <div key={t} className="term-results">
                  <h3>{termLabel(t)}</h3>
                  <Buckets
                    result={allResults[t]}
                    onConnect={setConnectTarget}
                    onCancelRequest={handleCancelRequest}
                    term={t}
                  />
                </div>
              )
          )}
      </div>

      {connectTarget && me && (
        <ConnectModal
          toUser={connectTarget}
          me={me}
          onClose={(shouldRefresh) => {
            setConnectTarget(null);
            if (shouldRefresh) runSearch();
          }}
        />
      )}
    </div>
  );
}